import PropTypes from 'prop-types';
import OtpInput from 'react-otp-input';
import { COLORS } from '../../../helpers/constants/styleguide';
import styled from 'styled-components';

/**
 * `numberOfInputs` is this component's name for the field count. Call sites pass
 * `numInputs` (react-otp-input's name), which this wrapper never reads, so the
 * count always falls back to its default.
 *
 * @typedef {Object} OTPInputProps
 * @property {string} [value]
 * @property {number} [numberOfInputs]
 * @property {(value: string) => void} [onChange]
 * @property {React.ReactNode} [separator]
 * @property {string} [placeholder]
 * @property {boolean} [shouldAutoFocus]
 * @property {number} [width]
 * @property {number} [height]
 * @property {React.CSSProperties} [inputStyle]
 * @property {React.CSSProperties} [containerStyle]
 * @property {string} [inputType]
 * @property {() => void} [onClick]
 */

/** @type {React.FC<OTPInputProps>} */
const OTPInput = ({
  value,
  numberOfInputs = 6,
  onChange,
  separator,
  placeholder,
  shouldAutoFocus,
  width = 55,
  height = 55,
  inputStyle,
  inputType,
  containerStyle,
  onClick = () => {},
}) => {
  return (
    <OtpInput
      value={value}
      onChange={onChange}
      numInputs={numberOfInputs}
      renderSeparator={separator}
      containerStyle={{ gap: 10, ...containerStyle }}
      inputStyle={{ width, height, ...inputStyle }}
      shouldAutoFocus={shouldAutoFocus}
      placeholder={placeholder}
      // TODO [sc-52382]: temporary exception — the label lives here because this
      // wrapper drops unknown props; removed when OTPPage (sc-52380) and phone
      // verification (sc-52382) adopt the shared mosaic field (sc-52376).
      renderInput={(inputProps, index) => (
        <Input
          onClick={onClick}
          {...inputProps}
          {...(index === 0 && { 'aria-label': 'Verification code' })}
          autoComplete="one-time-code"
        />
      )}
      inputType={inputType}
    />
  );
};

const Input = styled.input`
  border: 1px solid ${COLORS.medGrey};
  border-radius: 5px;
  &:focus {
    outline: none;
    box-shadow: 0px 0px 2px ${COLORS.azure};
  }

  /* Remove number input spinner */
  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

OTPInput.propTypes = {
  value: PropTypes.string,
  numberOfInputs: PropTypes.number,
  onChange: PropTypes.func,
  separator: PropTypes.node,
  placeholder: PropTypes.string,
  shouldAutoFocus: PropTypes.bool,
  inputType: PropTypes.string,
};

export default OTPInput;
